import { type FunctionComponent } from 'react';

import { Spinner } from '@blueprintjs/core';

import i18n from 'i18n/i18n';
import { IconDownload } from 'icons/svg/IconDownload';
import { type DunningSelectionForecast } from 'interfaces/Interfaces';
import { Button } from 'lib/Button';
import { testAttribute } from 'util/Util';

import DunningForecastTable from './DunningForecastTable';

interface DunningForecastProps {
  enabled: boolean;
  updatedAt?: string;
  forecast?: DunningSelectionForecast;
  forecastLoading: boolean;
  error?: unknown;
  downloadLoading: boolean;
  download: () => void;
  retry: () => void;
}

const DunningForecast: FunctionComponent<DunningForecastProps> = ({
  enabled,
  updatedAt,
  forecast,
  forecastLoading,
  error,
  download,
  downloadLoading,
  retry,
}) => {
  const updatedDate = updatedAt ? new Date(updatedAt) : undefined;
  const statusKey = `dunning-forecast.${enabled ? 'enabled' : 'disabled'}${updatedDate ? '-with-date' : ''}`;
  const statusText = i18n.t(
    statusKey,
    updatedDate
      ? {
          date: updatedDate.toLocaleDateString(i18n.language),
          time: updatedDate.toLocaleTimeString(i18n.language),
        }
      : undefined,
  );

  const content = (() => {
    if (!enabled) return null;

    if (forecastLoading) {
      return (
        <div className="dunning-forecast__spinner">
          <Spinner />
          <div>{i18n.t('dunning-forecast.loading')}</div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="dunning-forecast__message">
          <p>{i18n.t('dunning-forecast.error')}</p>
          <Button testId={testAttribute('xcy1', 'dunning-forecast.retry')} type="secondary" onClick={retry}>
            {i18n.t('dunning-forecast.retry')}
          </Button>
        </div>
      );
    }

    if (forecast?.withDunningSelection.total === 0) {
      return <div className="dunning-forecast__message">{i18n.t('dunning-forecast.no-results')}</div>;
    }

    return (
      <div className="dunning-forecast__display">
        {forecast && <DunningForecastTable forecast={forecast} />}
        <Button
          testId={testAttribute('xcy1', 'dunning-forecast.download')}
          icon={downloadLoading ? <Spinner size={16} /> : <IconDownload />}
          type="tertiary"
          onClick={download}
          disabled={downloadLoading || !forecast}>
          {i18n.t('dunning-forecast.download')}
        </Button>
      </div>
    );
  })();

  return (
    <div className="dunning-forecast">
      <div className="dunning-forecast__group">
        <p className="card-description dunning-forecast__status">{statusText}</p>
        {content}
      </div>
    </div>
  );
};

export default DunningForecast;
